/**
 * Stack overlay - renders a stack route as a slide-in panel.
 *
 * NORTHSTARS:
 * - Less code, better
 * - Trust the platform - one CSS transform on one element; enter, exit, drag
 *   and snap-back are all the same property, so the compositor animates them
 *   and nothing else in the tree can write to it mid-flight
 * - Invent nothing - AnimatePresence keeps the panel mounted while it exits,
 *   touch events drive the edge swipe
 */

import { Suspense, Component, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { usePresence } from "framer-motion";
import { matchPath, Routes, Route, useLocation } from "react-router-dom";
import { STACK_ROUTES } from "@/routes/routeObjects";
import {
  SCROLL_CONTAINER_OVERLAY_LAYER_STEP,
  ScrollContainerLayerProvider,
  useScrollContainerRegistration,
} from "@/contexts/ScrollContainerContext";
import { StackSurfaceContext } from "./StackSurfaceContext";
import type { StackEntry } from "@/contexts/NavigationStackContext";
import { isChunkLoadError, reloadOnceForChunkError } from "@/utils/lazy-with-retry";
import { resolveGestureAxis } from "@/lib/scroll/gestureAxisLock";
import { RouteGround } from "@/components/shared/RouteGround";
import { SECTION_TITLE } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface StackOverlayProps {
  entry: StackEntry;
  onDismiss: () => void;
  surfaceOrder: number;
  /** False while another stack entry is rendered over this one. */
  isTop: boolean;
}

// Error boundary for stack content
class StackErrorBoundary extends Component<
  { children: ReactNode; onDismiss: () => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode; onDismiss: () => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <h2 className={cn(SECTION_TITLE, "mb-2")}>Something went wrong</h2>
          <p className="text-muted-foreground mb-4">{this.state.error?.message || "An unexpected error occurred"}</p>
          <button onClick={this.props.onDismiss} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg">
            Go back
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Swipe threshold and velocity for dismissal
const SWIPE_THRESHOLD = 100;
const VELOCITY_THRESHOLD = 500;
const EDGE_ZONE_WIDTH = 24; // px from left edge that triggers back swipe

const OFFSCREEN = "translateX(100%)";
// At rest the panel carries no transform at all, so fixed descendants keep the
// viewport as their containing block exactly as before.
const HOME = "none";
const SLIDE_TRANSITION = "transform 250ms ease-out";
const SNAP_TRANSITION = "transform 180ms ease-out";
// If transitionend never fires (tab hidden, panel unmounted early), release anyway.
const EXIT_FALLBACK_MS = 400;

export function StackOverlay({ entry, onDismiss, surfaceOrder, isTop }: StackOverlayProps) {
  const scrollLayer = surfaceOrder * SCROLL_CONTAINER_OVERLAY_LAYER_STEP;
  const surfaceValue = useMemo(() => ({ isTop }), [isTop]);
  const matchedRoute = STACK_ROUTES.find(route => matchPath(route.path, entry.pathname));
  const RouteComponent = matchedRoute?.handle.component;
  const panelRef = useRef<HTMLDivElement>(null);
  const hasEnteredRef = useRef(false);
  const dragXRef = useRef(0);
  const isEdgeSwipeRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const startTimeRef = useRef(0);
  const gestureDecidedRef = useRef(false);
  const edgeZoneRef = useRef<HTMLDivElement>(null);
  const dragFrameRef = useRef<number | null>(null);
  const setOverlayScrollRef = useScrollContainerRegistration(
    Boolean(RouteComponent),
    scrollLayer,
  );

  const [isPresent, safeToRemove] = usePresence();
  const location = useLocation();
  const locationId = `${location.pathname}${location.search}${location.hash}`;

  const panelTargetRef = useRef<string>(OFFSCREEN);
  const movePanel = useCallback((transform: string, transition: string) => {
    const panel = panelRef.current;
    if (!panel) return;
    panelTargetRef.current = transform;
    panel.style.transition = transition;
    panel.style.transform = transform;
  }, []);

  // Enter and exit are the same transition on the same element. On mount the
  // panel is committed off-screen and released before first paint; when the
  // stack drops this entry, it slides out from wherever it is (mid-drag
  // included) and AnimatePresence unmounts it once the transition ends. If
  // the entry comes back while exiting, the same transition turns around.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    if (isPresent) {
      if (!hasEnteredRef.current) {
        hasEnteredRef.current = true;
        // An entry rebuilt after a reload is already where the user was: no slide.
        if (!entry.restored) {
          panel.style.transform = OFFSCREEN;
          panel.getBoundingClientRect();
        }
      }
      movePanel(HOME, SLIDE_TRANSITION);
      return;
    }

    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      safeToRemove?.();
    };
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target === panel && event.propertyName === "transform") release();
    };
    panel.addEventListener("transitionend", handleTransitionEnd);
    const fallback = window.setTimeout(release, EXIT_FALLBACK_MS);
    movePanel(OFFSCREEN, SLIDE_TRANSITION);

    return () => {
      panel.removeEventListener("transitionend", handleTransitionEnd);
      window.clearTimeout(fallback);
    };
  }, [entry.restored, isPresent, movePanel, safeToRemove]);

  // A dismiss leaves the panel at its swipe offset for the exit to pick up.
  // When the navigation lands on this same entry instead (a raw history.back
  // between duplicate entries), the panel is still present: bring it home.
  useEffect(() => {
    if (!isPresent || locationId !== entry.id || isEdgeSwipeRef.current) return;
    if (panelTargetRef.current === HOME) return;
    dragXRef.current = 0;
    gestureDecidedRef.current = false;
    movePanel(HOME, SNAP_TRANSITION);
  }, [entry.id, isPresent, location.key, locationId, movePanel]);

  const snapOverlayBack = () => {
    movePanel(HOME, SNAP_TRANSITION);
  };

  const scheduleOverlayX = (nextX: number) => {
    dragXRef.current = Math.max(0, nextX);
    if (dragFrameRef.current !== null) return;

    dragFrameRef.current = window.requestAnimationFrame(() => {
      dragFrameRef.current = null;
      movePanel(`translateX(${dragXRef.current}px)`, "none");
    });
  };

  const flushOverlayX = () => {
    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
    movePanel(`translateX(${dragXRef.current}px)`, "none");
  };

  const resetEdgeSwipe = () => {
    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
    dragXRef.current = 0;
    isEdgeSwipeRef.current = false;
    gestureDecidedRef.current = false;
    snapOverlayBack();
  };

  useEffect(() => {
    return () => {
      if (dragFrameRef.current !== null) {
        window.cancelAnimationFrame(dragFrameRef.current);
      }
    };
  }, []);

  // Edge zone touch handlers - these only fire on the left edge zone (touch-action: none)
  const handleEdgeTouchStart = (e: React.TouchEvent) => {
    startXRef.current = e.touches[0].clientX;
    startYRef.current = e.touches[0].clientY;
    startTimeRef.current = Date.now();
    dragXRef.current = 0;
    gestureDecidedRef.current = false;
    isEdgeSwipeRef.current = true;
  };

  const handleEdgeTouchMove = (e: React.TouchEvent) => {
    if (!isEdgeSwipeRef.current) return;

    const deltaX = e.touches[0].clientX - startXRef.current;
    const deltaY = e.touches[0].clientY - startYRef.current;

    // Wait for enough movement to decide gesture direction (shared arbiter)
    if (!gestureDecidedRef.current) {
      const axis = resolveGestureAxis(deltaX, deltaY);
      if (axis !== "undecided") {
        gestureDecidedRef.current = true;
        if (axis === "vertical") {
          resetEdgeSwipe();
          return;
        }
      }
    }

    if (gestureDecidedRef.current && deltaX > 0) {
      e.preventDefault(); // Prevent any scroll during confirmed back swipe
      scheduleOverlayX(deltaX);
    }
  };

  const handleEdgeTouchEnd = (e: React.TouchEvent) => {
    const elapsed = Date.now() - startTimeRef.current;
    const dragX = dragXRef.current;
    const totalMovement = Math.abs(dragX) + Math.abs(e.changedTouches[0].clientY - startYRef.current);

    // If it was a quick tap with minimal movement, pass through as click
    if (elapsed < 300 && totalMovement < 10) {
      const elementBelow = document.elementFromPoint(startXRef.current, startYRef.current);
      if (elementBelow && elementBelow !== edgeZoneRef.current) {
        // Dispatch click event to the element below
        const clickEvent = new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
          clientX: startXRef.current,
          clientY: startYRef.current,
        });
        elementBelow.dispatchEvent(clickEvent);
      }
      resetEdgeSwipe();
      return;
    }

    if (!isEdgeSwipeRef.current) {
      resetEdgeSwipe();
      return;
    }

    const velocity = elapsed > 0 ? (dragX / elapsed) * 1000 : 0;

    if (dragX > SWIPE_THRESHOLD || velocity > VELOCITY_THRESHOLD) {
      flushOverlayX();
      dragXRef.current = 0;
      isEdgeSwipeRef.current = false;
      gestureDecidedRef.current = false;
      onDismiss();
      return;
    }

    resetEdgeSwipe();
  };

  if (!matchedRoute || !RouteComponent) {
    return null;
  }

  return (
    <div
      ref={panelRef}
      data-stack-overlay
      className="fixed inset-0 z-[45] overflow-hidden lg:absolute lg:inset-0"
    >
      {/* The edge target sits at the panel's left edge; touches stay bound to it while the panel follows the finger. */}
      <div
        ref={edgeZoneRef}
        className="fixed bottom-0 left-0 top-0 z-[9999] touch-none"
        style={{ width: EDGE_ZONE_WIDTH }}
        onTouchStart={handleEdgeTouchStart}
        onTouchMove={handleEdgeTouchMove}
        onTouchEnd={handleEdgeTouchEnd}
        onTouchCancel={resetEdgeSwipe}
      />

      <div
        ref={setOverlayScrollRef}
        data-stack-overlay-surface
        className="relative h-full w-full overflow-x-hidden overflow-y-auto bg-background"
      >
        <StackSurfaceContext.Provider value={surfaceValue}>
        <ScrollContainerLayerProvider layer={scrollLayer}>
          <StackErrorBoundary onDismiss={onDismiss}>
            <Suspense fallback={<RouteGround />}>
              {/* Wrap in Routes to provide React Router context for useParams */}
              <Routes
                location={{
                  pathname: entry.pathname,
                  search: entry.search,
                  hash: entry.hash,
                  state: entry.state,
                  key: entry.id,
                }}
              >
                <Route path={matchedRoute.path} element={<RouteComponent />} />
              </Routes>
            </Suspense>
          </StackErrorBoundary>
        </ScrollContainerLayerProvider>
        </StackSurfaceContext.Provider>
      </div>
    </div>
  );
}
